import { AlertCircle, CheckCircle, Info, XCircle } from "lucide-react";
import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from "react";

export type ToastType = "success" | "error" | "info" | "warning";

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, "id">) => void;
  removeToast: (id: string) => void;
  showSuccess: (title: string, message?: string, duration?: number) => void;
  showError: (title: string, message?: string, duration?: number) => void;
  showInfo: (title: string, message?: string, duration?: number) => void;
  showWarning: (title: string, message?: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timeoutsRef = useRef<Map<string, number>>(new Map());

  const removeToast = useCallback((id: string) => {
    // Clear any pending timeout for this toast
    const timeout = timeoutsRef.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutsRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback(
    (toast: Omit<Toast, "id">) => {
      const id = `${Date.now()}-${Math.random()}`;
      const newToast: Toast = { ...toast, id };

      setToasts((prev) => [...prev, newToast]);

      // Auto-remove after duration (default 5s)
      const duration = toast.duration ?? 5000;
      if (duration > 0) {
        const timeout = setTimeout(() => {
          removeToast(id);
        }, duration);
        timeoutsRef.current.set(id, timeout);
      }
    },
    [removeToast]
  );

  const showSuccess = useCallback(
    (title: string, message?: string, duration?: number) => {
      addToast({ type: "success", title, message, duration });
    },
    [addToast]
  );

  const showError = useCallback(
    (title: string, message?: string, duration?: number) => {
      addToast({ type: "error", title, message, duration: duration ?? 7000 });
    },
    [addToast]
  );

  const showInfo = useCallback(
    (title: string, message?: string, duration?: number) => {
      addToast({ type: "info", title, message, duration });
    },
    [addToast]
  );

  const showWarning = useCallback(
    (title: string, message?: string, duration?: number) => {
      addToast({ type: "warning", title, message, duration });
    },
    [addToast]
  );

  return (
    <ToastContext.Provider
      value={{ toasts, addToast, removeToast, showSuccess, showError, showInfo, showWarning }}
    >
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

interface ToastContainerProps {
  toasts: Toast[];
  onRemove: (id: string) => void;
}

function ToastContainer({ toasts, onRemove }: ToastContainerProps) {
  if (toasts.length === 0) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-[9999] flex items-end justify-center p-4 md:justify-end">
      <div className="flex w-full max-w-md flex-col gap-2">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
        ))}
      </div>
    </div>
  );
}

interface ToastItemProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

function ToastItem({ toast, onRemove }: ToastItemProps) {
  const Icon = {
    success: CheckCircle,
    error: XCircle,
    info: Info,
    warning: AlertCircle,
  }[toast.type];

  const colorClasses = {
    success: "bg-green-50/80 dark:bg-green-950/80 border-green-200 dark:border-green-800",
    error: "bg-red-50/80 dark:bg-red-950/80 border-red-200 dark:border-red-800",
    info: "bg-blue-50/80 dark:bg-blue-950/80 border-blue-200 dark:border-blue-800",
    warning: "bg-yellow-50/80 dark:bg-yellow-950/80 border-yellow-200 dark:border-yellow-800",
  }[toast.type];

  const iconColorClasses = {
    success: "text-green-600 dark:text-green-400",
    error: "text-red-600 dark:text-red-400",
    info: "text-blue-600 dark:text-blue-400",
    warning: "text-yellow-600 dark:text-yellow-400",
  }[toast.type];

  const textColorClasses = {
    success: "text-green-900 dark:text-green-100",
    error: "text-red-900 dark:text-red-100",
    info: "text-blue-900 dark:text-blue-100",
    warning: "text-yellow-900 dark:text-yellow-100",
  }[toast.type];

  return (
    <div
      className={`pointer-events-auto flex items-start gap-3 rounded-lg border p-4 shadow-lg ${colorClasses} animate-in slide-in-from-right-full`}
      role="alert"
    >
      <Icon className={`h-5 w-5 flex-shrink-0 ${iconColorClasses}`} />
      <div className="flex-1 space-y-1 min-w-0">
        <div className={`font-semibold text-sm ${textColorClasses}`}>{toast.title}</div>
        {toast.message && (
          <div className={`text-xs ${textColorClasses} opacity-90 break-words`}>
            {toast.message}
          </div>
        )}
      </div>
      <button
        onClick={() => onRemove(toast.id)}
        className={`flex-shrink-0 rounded p-1 hover:bg-black/5 dark:hover:bg-white/5 ${textColorClasses}`}
        aria-label="Close"
      >
        <XCircle className="h-4 w-4" />
      </button>
    </div>
  );
}
